/*
 *
 *  * Copyright (c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { DiConfigService } from '@app/di/di-config.service';
import { NPRAuthentication } from '@app/di/security/npr-authentication.service';
import { AppConfigService } from '@app/common/app-config.service';
import { P2pNotificationService } from '@app/common/p2p-notification.service';
import { Base64Service } from '@app/util/base64.service';
import { TabContextService } from '@app/ui/client-cache/tab-context.service';
import { AuditService } from '@app/common/audit.service';
import { appConstants } from '@app/common/app-constants';
import { REPORT_STATUS } from '@app/di/integrations/reporting/reporting-contants';

const log = new Logger('NPRDataService');

@Injectable({
  providedIn: 'root'
})
export class NPRDataService {
  private reportType = '';

  private radiologistId: string;
  private radiologistUserName: string;
  private radiologistDomain: string;

  public afterSyncReport: any;

  constructor(
    private authenticator: NPRAuthentication,
    private base64Service: Base64Service,
    private dataService: ProxyDataService,
    private translate: TranslateService,
    private diConfigService: DiConfigService,
    private appConfigService: AppConfigService,
    private messenger: P2pNotificationService,
    private tabContextService: TabContextService,
    private auditService: AuditService
  ) {}

  public init(option?: string) {
    this.radiologistId = this.appConfigService.getUserDto().userId;
    this.radiologistUserName = this.appConfigService.getUserId();
    this.radiologistDomain = this.appConfigService.getUserDefaultDomain();

    this.reportType = this.diConfigService.DI_CONFIG.REPORTING_INTEGRATION_TYPE;
    if (option === 'UseSystemToken') {
      this.authenticator.useSystemToken();
    }
  }

  private getAuthorization() {
    return 'Bearer ' + this.authenticator.getToken();
  }

  public getConfiguration() {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      })
    };
    return this.dataService.httpGet('/npr/references/config?' + Date.now(), options);
  }

  public getReferences(category: string, params?: any) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      }),
      params: params !== undefined ? new HttpParams(params) : undefined
    };
    return this.dataService.httpGet(`/npr/references/${category}`, options);
  }

  public getReport(examId: number) {
    log.debug('GET ReportAPI called for exam id: ', examId);
    const requestId = this.authenticator.createUUID();
    const options = this.headerToAcceptPdf(requestId);
    return this.dataService.httpGet('/npr/report/' + examId, options).then(
      response => {
        log.debug('invoke GET ReportAPI for exam (', examId, ') - success with response:', response);
        const data = this.base64Service.bytesToBase64(new Uint8Array(response));
        return data;
      },
      error => {
        log.error('invoke GET ReportAPI for exam (', examId, ') - failed, details:', error);
        throw error;
      }
    );
  }

  /***
   * Get Call API - Report Lifecycle
   * https://localhost:8443/prs/api/v1/report/{{reportId}}/lifecycle
   * @param reportId
   * @returns json array (authorId, statusCode, createdOn)
   */
  public getReportLifecycle(reportId: number) {
    log.debug('GET Report Lifecycle called for report id: ', reportId);
    const requestId = this.authenticator.createUUID();
    const options = this.headers(requestId);
    return this.dataService.httpGet('/npr/report/' + reportId + '/lifecycle', options).then(
      response => {
        log.debug('invoke GET Report Lifecycle API for report id (', reportId, ') - success with response:', response);
        return response;
      },
      error => {
        log.error('invoke GET Report Lifecycle API for report id (', reportId, ') - failed, details:', error);
        throw error;
      }
    );
  }

  public checkClientService(url: string) {
    const options = {
      responseType: 'text',
      headers: new HttpHeaders({
        Accept: '*/*',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      })
    };
    return this.dataService.httpGet(url, options);
  }

  /**
   * This temporary API is used as workaround solution for the missing associated exams in openReport's response
   * This will be removed in future when proper fix is done on SmartReporting
   */
  public getResource(url: string) {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
        Authorization: this.getAuthorization(),
        Pragma: 'no-cache'
      })
    };
    return this.dataService.httpGet(url, options);
  }

  private headerToAcceptPdf(requestId?: string, responseType?: any) {
    const options = {
      responseType: !_.isUndefined(responseType) ? responseType : 'arraybuffer',
      headers: new HttpHeaders({
        Accept: 'application/pdf',
        'Cache-Control': 'no-cache',
        Authorization: this.getAuthorization(),
        'X-Request-ID': _.isEmpty(requestId) ? '' : requestId,
        Pragma: 'no-cache'
      })
    };
    return options;
  }

  private headers(requestId?: string) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Authorization: this.getAuthorization(),
        'X-Request-ID': requestId ? requestId : '',
        Pragma: 'no-cache'
      })
    };
    return options;
  }

  private post(operation: string, data: any, requestId?: string): Promise<any> {
    log.debug('ReportAPI called for', operation, ', Data:', data);
    const options = this.headers(requestId);
    return this.dataService.httpPost('/npr/report/' + operation, data, options).then(
      response => {
        log.debug('invoke(', operation, ') - success with response:', response);
        return response;
      },
      error => {
        log.error('invoke(', operation, ') - failed, details:', error);
        throw error;
      }
    );
  }

  public createReport(examId: number, accession: string, requestId?: string, isLegacy?: boolean) {
    const params = {
      type: this.reportType,
      author: this.radiologistUserName,
      organization: this.radiologistDomain,
      examId: examId,
      accessionNumber: accession,
      status: isLegacy ? REPORT_STATUS.ADDENDUM_PENDING : REPORT_STATUS.PENDING
    };
    if (!accession) {
      params.accessionNumber = this.translate.instant('label.temp') + examId;
      params.status = REPORT_STATUS.PENDING_TEMPORARY;
    }
    return this.post('create', params, requestId).then(
      response => {
        const reportId = parseInt(response.results[0].reportId);
        this.syncReportStatus(reportId, params.examId, params.status);
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Create,
          isLegacy
            ? this.auditService.getActionType().ReportCreationOnLegacy
            : this.auditService.getActionType().ReportCreation,
          this.auditService.getEventOutcome().Success,
          reportId,
          appConstants.applicationServerParticipant.NPR,
          params.status,
          undefined,
          undefined,
          params.examId
        );
        return response;
      },
      error => {
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Create,
          isLegacy
            ? this.auditService.getActionType().ReportCreationOnLegacyFailure
            : this.auditService.getActionType().ReportCreationFailure,
          this.auditService.getEventOutcome().SeriousFailure,
          undefined,
          appConstants.applicationServerParticipant.NPR,
          params.status,
          undefined,
          undefined,
          params.examId
        );
        throw error;
      }
    );
  }

  // Function is obselete
  public createLegacyReport(examId: number, accession: string, requestId?: string) {
    const params = {
      type: this.reportType,
      author: this.radiologistUserName,
      organization: this.radiologistDomain,
      examId: examId,
      accessionNumber: accession,
      status: 9001
    };
    return this.post('create', params, requestId).then(
      response => {
        const reportId = parseInt(response.results[0].reportId);
        this.syncReportStatus(reportId, params.examId, params.status);
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Create,
          this.auditService.getActionType().ReportCreationOnLegacy,
          this.auditService.getEventOutcome().Success,
          reportId,
          appConstants.applicationServerParticipant.NPR,
          params.status,
          undefined,
          undefined,
          params.examId
        );
        return response;
      },
      error => {
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Create,
          this.auditService.getActionType().ReportCreationOnLegacyFailure,
          this.auditService.getEventOutcome().SeriousFailure,
          undefined,
          appConstants.applicationServerParticipant.NPR,
          params.status,
          undefined,
          undefined,
          params.examId
        );
        throw error;
      }
    );
  }

  public updateReport(reportId: string, accession: string, examId: number, status: number, requestId?: string) {
    const params = {
      action: 'saveReport',
      reportId: reportId,
      associatedExams: [
        {
          accessionNumber: accession,
          examId: examId,
          organization: this.radiologistDomain
        }
      ],
      reportStatus: status,
      userId: this.radiologistUserName
    };
    return this.post('update', params, requestId).then(
      response => {
        this.syncReportStatus(parseInt(reportId), examId, status);
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Update,
          this.auditService.getActionType().ReportUpdation,
          this.auditService.getEventOutcome().Success,
          reportId,
          appConstants.applicationServerParticipant.NPR,
          undefined,
          undefined,
          undefined,
          examId
        );
        return response;
      },
      error => {
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Update,
          this.auditService.getActionType().ReportUpdationFailure,
          this.auditService.getEventOutcome().MajorFailure,
          reportId,
          appConstants.applicationServerParticipant.NPR,
          undefined,
          undefined,
          undefined,
          examId
        );
        throw error;
      }
    );
  }

  public async deleteReport(reportId: string, accession: string, examId: number, requestId?: string) {
    const params = {
      action: 'deleteReport',
      reportId: reportId,
      associatedExams: [
        {
          accessionNumber: accession,
          examId: examId,
          organization: this.radiologistDomain
        }
      ],
      userId: this.radiologistUserName
    };
    const syncedExamsList = await this.getSyncedExamList(examId);
    return this.post('update', params, requestId).then(
      response => {
        this.syncReportStatus(undefined, syncedExamsList, 0);
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Delete,
          this.auditService.getActionType().ReportDeletion,
          this.auditService.getEventOutcome().Success,
          reportId,
          appConstants.applicationServerParticipant.NPR,
          undefined,
          undefined,
          undefined,
          examId
        );
        return response;
      },
      error => {
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Delete,
          this.auditService.getActionType().ReportDeletionFailure,
          this.auditService.getEventOutcome().SeriousFailure,
          reportId,
          appConstants.applicationServerParticipant.NPR,
          undefined,
          undefined,
          undefined,
          examId
        );
        throw error;
      }
    );
  }

  private getSyncedExamList(examId) {
    return new Promise(resolve => {
      let syncedExamsList;
      if (this.diConfigService.isAssociationEnabled()) {
        syncedExamsList = this.tabContextService.getAssociatedIds(examId);
        if (syncedExamsList) {
          syncedExamsList.push(examId);
        } else {
          syncedExamsList = [examId];
        }
        resolve(syncedExamsList);
      } else {
        this.getAssociationList(examId).then(
          response => {
            syncedExamsList = response.map(exam => exam.examId);
            resolve(syncedExamsList);
          },
          _ => {
            syncedExamsList = [examId];
            resolve(syncedExamsList);
          }
        );
      }
    });
  }

  public discardReport(item: any) {
    return this.post('discard-emergency', item, 'emergency').then(
      response => {
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Delete,
          this.auditService.getActionType().ReportEmergencyDiscard,
          this.auditService.getEventOutcome().Success,
          item.reportId,
          appConstants.applicationServerParticipant.NPR,
          undefined,
          undefined,
          undefined,
          item.examId
        );
        return response;
      },
      error => {
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Delete,
          this.auditService.getActionType().ReportEmergencyDiscardFailure,
          this.auditService.getEventOutcome().SeriousFailure,
          item.reportId,
          appConstants.applicationServerParticipant.NPR,
          undefined,
          undefined,
          undefined,
          item.examId
        );
        log.debug('Emegergency discard failed with', error);
        throw error;
      }
    );
  }

  public discardReports(items: any, requestId?: string) {
    return this.post('discard-all', items, requestId);
  }

  public saveReports(items: any, requestId?: string) {
    return this.post('save-all', items, requestId);
  }

  private put(operation: string, data: any, requestId?: string): Promise<any> {
    log.debug('Send PUT request to Report API service');
    const options = this.headers(requestId);
    return this.dataService.httpPut('/npr/report/' + operation, data, options).then(
      response => {
        log.debug('invoke(', operation, ') - success with response:', response);
        return response;
      },
      error => {
        log.error('invoke(', operation, ') - failed, details:', error);
        throw error;
      }
    );
  }

  public async saveReport(reportId: string, accession: string, examId: number, status: number, requestId?: string) {
    const params = {
      type: this.reportType,
      author: this.radiologistUserName,
      organization: this.radiologistDomain,
      reportId: reportId,
      accessionNumber: accession,
      examId: examId,
      status: status
    };
    const syncedExamsList = await this.getSyncedExamList(examId);
    return this.put('save', params, requestId).then(
      response => {
        this.syncReportStatus(reportId, syncedExamsList, status);
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Execute,
          this.auditService.getActionType().ReportSave,
          this.auditService.getEventOutcome().Success,
          reportId,
          appConstants.applicationServerParticipant.NPR,
          status,
          undefined,
          undefined,
          examId
        );
        return response;
      },
      error => {
        this.auditService.saveReportAuditAction(
          this.auditService.getActionCode().Execute,
          this.auditService.getActionType().ReportSaveFailure,
          this.auditService.getEventOutcome().SeriousFailure,
          reportId,
          appConstants.applicationServerParticipant.NPR,
          status,
          undefined,
          undefined,
          examId
        );
        throw error;
      }
    );
  }

  public relinkReport(reportId: string, examId: number, accession: string, issuerId?: string) {
    return this.put(reportId + '/exams', [
      {
        reportId: reportId,
        accessionNumber: accession,
        issuerId: issuerId,
        examId: examId
      }
    ]);
  }

  private publishReportStatus(reportId, ids, statusId) {
    const data = {
      ids: ids,
      reportId: parseInt(reportId),
      statusId: statusId,
      authorId: statusId !== 0 ? this.radiologistId : undefined,
      authorName: statusId !== 0 ? this.radiologistUserName : undefined,
      sender: this.radiologistUserName,
      receiver: '*'
    };
    this.messenger.notifyP2P('', 'ReportSync', data).then(
      _response => log.info('Messenger - p2p notification sent', data),
      error => log.error('Messenger - p2p notification failed', error)
    );
  }

  public syncReportStatus(reportId, examId, status) {
    if (!Array.isArray(examId)) {
      examId = [examId];
    }
    this.publishReportStatus(reportId, examId, status);
    this.afterSyncReport && this.afterSyncReport(reportId, examId, status);
  }

  public syncAssociation(associationList: any, status?: number, requestId?: string) {
    const params = {
      author: this.radiologistUserName,
      data: associationList,
      reportType: this.reportType
    };
    const options = this.headers(requestId);
    return this.dataService.httpPut('/npr/association', params, options).then(
      response => {
        log.info('syncAssociation success with response:', response);
        if (status) {
          if (response.results.length > 0) {
            const reportId = response.results[0].reportId;
            const examIds = response.results.map(exam => exam.examId);
            this.syncReportStatus(reportId, examIds, status);
          }
        }
        return response.results;
      },
      error => {
        log.error('syncAssociation failed, details:', error);
        throw error;
      }
    );
  }

  public removeFromAssociation(examId: number, requestId?: string) {
    const options = {
      headers: new HttpHeaders({
        'Cache-Control': 'no-cache',
        Authorization: this.getAuthorization(),
        'X-Request-ID': requestId ? requestId : '',
        Pragma: 'no-cache'
      })
    };
    return this.dataService.httpDelete('/npr/association/' + examId, options).then(
      response => {
        log.info('removeFromAssociation success with response:', response);
        this.syncReportStatus(undefined, examId, 0);
        return response;
      },
      error => {
        if (!this.authenticator.isSystemToken()) {
          log.error('removeFromAssociation failed, details:', error);
          throw error;
        } else {
          log.debug('removeFromAssociation failed, details:', error);
        }
      }
    );
  }

  public getAssociationList(examId: number, requestId?: string) {
    const options = this.headers(requestId);
    return this.dataService.httpGet('/npr/association/' + examId, options).then(
      response => {
        log.info('getAssociationList success with response:', response);
        return response;
      },
      error => {
        log.error('getAssociationList failed, details:', error);
        throw error;
      }
    );
  }

  public addToAssociation(reportId: number, accession: string, examId: number, status?: number, requestId?: string) {
    const params = {
      accessionNumber: accession,
      examId: examId
    };
    const options = this.headers(requestId);
    return this.dataService.httpPut('/npr/association/' + reportId + '/add', params, options).then(
      response => {
        log.info('addToAssociation success with response:', response);
        if (status) {
          this.syncReportStatus(reportId, params.examId, status);
        }
        return response;
      },
      error => {
        log.error('addToAssociation failed, details:', error);
        throw error;
      }
    );
  }

  public getReportStatus(examIds) {
    const requestId = this.authenticator.createUUID();
    const options = this.headers(requestId);
    return this.dataService.httpGet('/npr/status?ids=' + examIds, options).then(
      response => {
        log.info('getReportStatus - success with response :', response);
        return response;
      },
      error => {
        log.info('getReportStatus - failed, details :', error);
        throw error;
      }
    );
  }
}
